import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../services/api';
import { useWebSocket } from '../hooks/useWebSocket';
import { useToastContext } from '../App';

function ServerDetail() {
  const { id } = useParams();
  const [server, setServer] = useState(null);
  const [activeTab, setActiveTab] = useState('console');
  const [commandInput, setCommandInput] = useState('');
  const [commandHistory, setCommandHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const consoleEndRef = useRef(null);
  const addToast = useToastContext();

  const { connected, consoleLines, serverStatus, chatEntries, players: wsPlayers, sendCommand, initPlayers, initChatHistory } = useWebSocket(id);

  const loadServer = useCallback(async () => {
    try {
      const data = await api.getServer(id);
      setServer(data);
    } catch (e) {
      console.error(e);
    }
  }, [id]);

  useEffect(() => {
    loadServer();
    const interval = setInterval(loadServer, 5000);
    return () => clearInterval(interval);
  }, [loadServer]);

  useEffect(() => {
    if (serverStatus) {
      setServer(prev => {
        if (!prev || prev.status === serverStatus) return prev;
        return { ...prev, status: serverStatus };
      });
    }
  }, [serverStatus]);

  // Sync WS player list from server data (fallback when WS hasn't caught up)
  useEffect(() => {
    if (server && server.players) {
      initPlayers(server.players);
    }
  }, [server, initPlayers]);

  useEffect(() => {
    if (consoleEndRef.current) {
      consoleEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [consoleLines]);

  function handleCommand(e) {
    e.preventDefault();
    if (!commandInput.trim()) return;
    sendCommand(commandInput.trim());
    setCommandHistory(prev => [commandInput.trim(), ...prev.slice(0, 49)]);
    setCommandInput('');
    setHistoryIndex(-1);
  }

  function handleKeyDown(e) {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      const newIndex = Math.min(historyIndex + 1, commandHistory.length - 1);
      setHistoryIndex(newIndex);
      if (commandHistory[newIndex]) setCommandInput(commandHistory[newIndex]);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const newIndex = Math.max(historyIndex - 1, -1);
      setHistoryIndex(newIndex);
      setCommandInput(newIndex >= 0 ? commandHistory[newIndex] : '');
    }
  }

  async function handleStart() {
    try {
      await api.startServer(id);
      addToast('Server wird gestartet...', 'info');
      loadServer();
    } catch (e) {
      addToast(e.message, 'error');
    }
  }

  async function handleStop() {
    try {
      await api.stopServer(id);
      addToast('Server wird gestoppt...', 'info');
      loadServer();
    } catch (e) {
      addToast(e.message, 'error');
    }
  }

  async function handleKill() {
    if (!window.confirm('Server wirklich erzwungen beenden? Dies kann zu Datenverlust fuehren!')) return;
    try {
      await api.killServer(id);
      addToast('Server wurde beendet', 'warning');
      loadServer();
    } catch (e) {
      addToast(e.message, 'error');
    }
  }

  function getLineClass(text) {
    if (text.startsWith('>')) return 'log-line command';
    if (text.includes('WARN')) return 'log-line warn';
    if (text.includes('ERROR') || text.includes('Exception')) return 'log-line error';
    if (text.includes('INFO')) return 'log-line info';
    return 'log-line';
  }

  if (!server) {
    return <div className="empty-state"><div className="spinner" /></div>;
  }

  return (
    <div>
      <div className="page-header">
        <div className="flex items-center gap-3">
          <Link to="/servers" className="btn btn-secondary btn-sm">&larr; Zurueck</Link>
          <h2>{server.name}</h2>
          <span className={`status-badge ${server.status}`}>
            <span className="status-dot" />
            {server.status}
          </span>
          {connected && <span className="tag enabled">WS verbunden</span>}
        </div>
        <div className="flex gap-2">
          {server.status === 'stopped' && (
            <button className="btn btn-primary" onClick={handleStart}>Starten</button>
          )}
          {server.status === 'running' && (
            <>
              <button className="btn btn-danger" onClick={handleStop}>Stoppen</button>
              <button className="btn btn-warning btn-sm" onClick={handleKill}>Kill</button>
            </>
          )}
          {(server.status === 'starting' || server.status === 'stopping') && (
            <button className="btn btn-warning btn-sm" onClick={handleKill}>Erzwingen</button>
          )}
        </div>
      </div>

      {/* Server Info Bar */}
      <div className="grid grid-4 mb-4">
        <div className="stat-card">
          <div className="stat-value" style={{ fontSize: '20px' }}>{server.port}</div>
          <div className="stat-label">Port</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ fontSize: '20px' }}>{server.maxRam}</div>
          <div className="stat-label">Max RAM</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ fontSize: '20px' }}>{server.players?.length || 0}</div>
          <div className="stat-label">Spieler online</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ fontSize: '20px' }}>
            {server.uptime > 0 ? formatUptime(server.uptime) : '--'}
          </div>
          <div className="stat-label">Uptime</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs">
        <button className={`tab ${activeTab === 'console' ? 'active' : ''}`} onClick={() => setActiveTab('console')}>
          Konsole
        </button>
        <button className={`tab ${activeTab === 'players' ? 'active' : ''}`} onClick={() => setActiveTab('players')}>
          Spieler ({server.players?.length || 0})
        </button>
        <button className={`tab ${activeTab === 'chat' ? 'active' : ''}`} onClick={() => setActiveTab('chat')}>
          Chat-Verlauf
        </button>
        <button className={`tab ${activeTab === 'mods' ? 'active' : ''}`} onClick={() => setActiveTab('mods')}>
          Mods
        </button>
        <button className={`tab ${activeTab === 'world' ? 'active' : ''}`} onClick={() => setActiveTab('world')}>
          Welt
        </button>
        {server.bluemapPort && (
          <button className={`tab ${activeTab === 'bluemap' ? 'active' : ''}`} onClick={() => setActiveTab('bluemap')}>
            Karte
          </button>
        )}
        <button className={`tab ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => setActiveTab('settings')}>
          Einstellungen
        </button>
      </div>

      {/* Console Tab */}
      {activeTab === 'console' && (
        <div className="console-container">
          <div className="console-output">
            {consoleLines.map((line, i) => (
              <div key={i} className={getLineClass(line.text)}>{line.text}</div>
            ))}
            <div ref={consoleEndRef} />
          </div>
          <form onSubmit={handleCommand} className="console-input-row">
            <span className="prefix">&gt;</span>
            <input
              value={commandInput}
              onChange={e => setCommandInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Befehl eingeben..."
              disabled={server.status !== 'running'}
              autoFocus
            />
          </form>
        </div>
      )}

      {/* Players Tab */}
      {activeTab === 'players' && (
        <ServerPlayers serverId={id} players={server.players || []} />
      )}

      {/* Chat Tab */}
      {activeTab === 'chat' && (
        <ServerChat
          serverId={id}
          chatEntries={chatEntries}
          wsPlayers={wsPlayers}
          serverPlayers={server.players || []}
          connected={connected}
          initPlayers={initPlayers}
          initChatHistory={initChatHistory}
          isRunning={server.status === 'running'}
        />
      )}

      {/* Mods Tab */}
      {activeTab === 'mods' && (
        <ServerMods serverId={id} />
      )}

      {/* World Tab */}
      {activeTab === 'world' && (
        <ServerWorld server={server} />
      )}

      {/* BlueMap Tab */}
      {activeTab === 'bluemap' && server.bluemapPort && (
        <ServerBlueMap server={server} />
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <ServerSettings server={server} onUpdate={loadServer} />
      )}
    </div>
  );
}

function ServerChat({ serverId, chatEntries, wsPlayers, serverPlayers, connected, initPlayers, initChatHistory, isRunning }) {
  const [chatLoading, setChatLoading] = useState(false);
  const [chatLoaded, setChatLoaded] = useState(false);
  const chatEndRef = useRef(null);

  // Load chat history from API on first mount
  useEffect(() => {
    if (!chatLoaded) {
      setChatLoading(true);
      api.getChatHistory(serverId, 200)
        .then(history => {
          initChatHistory(history);
          setChatLoaded(true);
        })
        .catch(e => {
          console.error('Failed to load chat history:', e);
          initChatHistory([]);
          setChatLoaded(true);
        })
        .finally(() => setChatLoading(false));
    }
  }, [serverId, chatLoaded, initChatHistory]);

  // Auto-scroll chat to bottom when new entries arrive
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatEntries]);

  // Use wsPlayers if available, fall back to serverPlayers
  const activePlayers = wsPlayers.length > 0 ? wsPlayers : serverPlayers;

  return (
    <div className="card">
      <div className="guest-detail-columns">
        {/* Player list sidebar */}
        <div className="guest-player-panel">
          <h3>Spieler online ({activePlayers.length})</h3>
          {activePlayers.length === 0 ? (
            <div className="guest-empty-hint">
              {isRunning ? 'Keine Spieler online' : 'Server ist offline'}
            </div>
          ) : (
            <ul className="guest-player-list">
              {activePlayers.map(p => (
                <li key={p} className="guest-player-entry">
                  <img
                    className="player-avatar"
                    src={`https://mc-heads.net/avatar/${p}/24`}
                    alt={p}
                    onError={e => { e.target.style.display = 'none'; }}
                    style={{ width: 24, height: 24, marginRight: 8, imageRendering: 'pixelated' }}
                  />
                  {p}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Chat history */}
        <div className="guest-chat-panel">
          <h3>Chat-Verlauf {connected && <span className="ws-connected-dot" title="Live-Verbindung aktiv" />}</h3>
          <div className="guest-chat-container">
            {chatLoading ? (
              <div className="guest-empty-hint">Lade Chat-Verlauf...</div>
            ) : chatEntries.length === 0 ? (
              <div className="guest-empty-hint">Noch keine Nachrichten</div>
            ) : (
              chatEntries.map((entry, idx) => (
                <ChatEntry key={`${entry.ts}-${idx}`} entry={entry} />
              ))
            )}
            <div ref={chatEndRef} />
          </div>
        </div>
      </div>
    </div>
  );
}

function ChatEntry({ entry }) {
  const time = new Date(entry.ts).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });

  switch (entry.type) {
    case 'chat':
      return (
        <div className="chat-line chat-msg">
          <span className="chat-time">{time}</span>
          <span className="chat-player">&lt;{entry.player}&gt;</span>
          <span className="chat-text">{entry.message}</span>
        </div>
      );
    case 'join':
      return (
        <div className="chat-line chat-join">
          <span className="chat-time">{time}</span>
          <span>{entry.player} ist beigetreten</span>
        </div>
      );
    case 'leave':
      return (
        <div className="chat-line chat-leave">
          <span className="chat-time">{time}</span>
          <span>{entry.player} hat das Spiel verlassen</span>
        </div>
      );
    case 'start':
      return (
        <div className="chat-line chat-start">
          <span className="chat-time">{time}</span>
          <span>--- Server gestartet ---</span>
        </div>
      );
    case 'stop':
      return (
        <div className="chat-line chat-stop">
          <span className="chat-time">{time}</span>
          <span>--- Server gestoppt ---</span>
        </div>
      );
    case 'restart':
      return (
        <div className="chat-line chat-restart">
          <span className="chat-time">{time}</span>
          <span>--- Server neugestartet ---</span>
        </div>
      );
    default:
      return null;
  }
}

function ServerPlayers({ serverId, players }) {
  const addToast = useToastContext();

  async function handleKick(name) {
    try {
      await api.kickPlayer(serverId, name);
      addToast(`${name} gekickt`, 'success');
    } catch (e) {
      addToast(e.message, 'error');
    }
  }

  async function handleBan(name) {
    const reason = window.prompt(`Grund fuer Ban von ${name}:`, 'Banned');
    if (reason === null) return;
    try {
      await api.banPlayer(serverId, name, reason);
      addToast(`${name} gebannt`, 'success');
    } catch (e) {
      addToast(e.message, 'error');
    }
  }

  async function handleOp(name) {
    try {
      await api.opPlayer(serverId, name);
      addToast(`${name} ist jetzt Operator`, 'success');
    } catch (e) {
      addToast(e.message, 'error');
    }
  }

  return (
    <div className="card">
      <h3>Online Spieler</h3>
      {players.length === 0 ? (
        <div className="empty-state">
          <p>Keine Spieler online</p>
        </div>
      ) : (
        <div>
          {players.map(name => (
            <div key={name} className="player-item">
              <div className="player-info">
                <img
                  className="player-avatar"
                  src={`https://mc-heads.net/avatar/${name}/36`}
                  alt={name}
                  onError={e => { e.target.style.display = 'none'; }}
                />
                <span className="fw-bold">{name}</span>
              </div>
              <div className="player-actions">
                <button className="btn btn-secondary btn-sm" onClick={() => handleOp(name)}>OP</button>
                <button className="btn btn-warning btn-sm" onClick={() => handleKick(name)}>Kick</button>
                <button className="btn btn-danger btn-sm" onClick={() => handleBan(name)}>Ban</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ServerMods({ serverId }) {
  const [mods, setMods] = useState([]);
  const [library, setLibrary] = useState([]);
  const [showInstall, setShowInstall] = useState(false);
  const addToast = useToastContext();

  useEffect(() => {
    loadMods();
  }, [serverId]);

  async function loadMods() {
    try {
      const [serverMods, libMods] = await Promise.all([
        api.getServerMods(serverId),
        api.getModLibrary(),
      ]);
      setMods(serverMods);
      setLibrary(libMods);
    } catch (e) {
      console.error(e);
    }
  }

  async function handleToggle(fileName) {
    try {
      await api.toggleMod(serverId, fileName);
      addToast('Mod-Status geaendert (Neustart noetig)', 'info');
      loadMods();
    } catch (e) {
      addToast(e.message, 'error');
    }
  }

  async function handleRemove(fileName) {
    if (!window.confirm(`Mod "${fileName}" entfernen?`)) return;
    try {
      await api.removeModFromServer(serverId, fileName);
      addToast('Mod entfernt', 'success');
      loadMods();
    } catch (e) {
      addToast(e.message, 'error');
    }
  }

  async function handleInstall(fileName) {
    try {
      await api.installModToServer(serverId, fileName);
      addToast('Mod installiert (Neustart noetig)', 'success');
      setShowInstall(false);
      loadMods();
    } catch (e) {
      addToast(e.message, 'error');
    }
  }

  return (
    <div className="card">
      <div className="card-header">
        <h3>Server Mods</h3>
        <button className="btn btn-primary btn-sm" onClick={() => setShowInstall(true)}>
          + Mod installieren
        </button>
      </div>

      {mods.length === 0 ? (
        <div className="empty-state">
          <p>Keine Mods installiert</p>
        </div>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Dateiname</th>
                <th>Status</th>
                <th>Groesse</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {mods.map(mod => (
                <tr key={mod.fileName}>
                  <td>{mod.fileName.replace('.disabled', '')}</td>
                  <td>
                    <span className={`tag ${mod.enabled ? 'enabled' : 'disabled'}`}>
                      {mod.enabled ? 'Aktiv' : 'Deaktiviert'}
                    </span>
                  </td>
                  <td>{formatSize(mod.size)}</td>
                  <td>
                    <div className="flex gap-2">
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => handleToggle(mod.fileName)}
                      >
                        {mod.enabled ? 'Deaktivieren' : 'Aktivieren'}
                      </button>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleRemove(mod.fileName)}
                      >
                        Entfernen
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showInstall && (
        <div className="modal-overlay" onClick={() => setShowInstall(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>Mod aus Bibliothek installieren</h3>
            {library.length === 0 ? (
              <div className="empty-state">
                <p>Keine Mods in der Bibliothek. Gehe zum Mod-Manager um Mods herunterzuladen.</p>
              </div>
            ) : (
              <div>
                {library.map(mod => (
                  <div key={mod.fileName} className="player-item">
                    <span>{mod.fileName}</span>
                    <button className="btn btn-primary btn-sm" onClick={() => handleInstall(mod.fileName)}>
                      Installieren
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowInstall(false)}>Schliessen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ServerSettings({ server, onUpdate }) {
  const [form, setForm] = useState({
    name: server.name,
    port: server.port,
    minRam: server.minRam,
    maxRam: server.maxRam,
    javaPath: server.javaPath,
    autoRestart: server.autoRestart,
    jvmArgs: server.jvmArgs || '',
  });
  const addToast = useToastContext();

  async function handleSave(e) {
    e.preventDefault();
    try {
      await api.updateServer(server.id, { ...form, port: parseInt(form.port) });
      addToast('Einstellungen gespeichert', 'success');
      onUpdate();
    } catch (e) {
      addToast(e.message, 'error');
    }
  }

  return (
    <div className="card">
      <h3>Server Einstellungen</h3>
      <p className="text-sm text-muted mb-4">Server muss gestoppt sein um Einstellungen zu aendern.</p>
      <form onSubmit={handleSave}>
        <div className="form-group">
          <label>Server Name</label>
          <input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Port</label>
            <input className="form-input" type="number" value={form.port} onChange={e => setForm({ ...form, port: e.target.value })} />
          </div>
          <div className="form-group">
            <label>Java Pfad</label>
            <input className="form-input" value={form.javaPath} onChange={e => setForm({ ...form, javaPath: e.target.value })} />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Min RAM</label>
            <input className="form-input" value={form.minRam} onChange={e => setForm({ ...form, minRam: e.target.value })} />
          </div>
          <div className="form-group">
            <label>Max RAM</label>
            <input className="form-input" value={form.maxRam} onChange={e => setForm({ ...form, maxRam: e.target.value })} />
          </div>
        </div>
        <div className="form-group">
          <label>JVM Argumente</label>
          <input className="form-input" value={form.jvmArgs} onChange={e => setForm({ ...form, jvmArgs: e.target.value })} />
        </div>
        <div className="form-group">
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input type="checkbox" checked={form.autoRestart} onChange={e => setForm({ ...form, autoRestart: e.target.checked })} />
            Auto-Restart bei Crash
          </label>
        </div>
        <button type="submit" className="btn btn-primary" disabled={server.status !== 'stopped'}>
          Speichern
        </button>
      </form>
    </div>
  );
}

function ServerWorld({ server }) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [downloading, setDownloading] = useState(false);
  const fileInputRef = useRef(null);
  const addToast = useToastContext();

  async function handleDownload() {
    setDownloading(true);
    try {
      const url = api.getWorldDownloadUrl(server.id);
      // Use a hidden anchor to trigger download with auth
      const token = sessionStorage.getItem('mcmon_token');
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error(err.error || 'Download fehlgeschlagen');
      }
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      // Try to get filename from Content-Disposition header
      const cd = res.headers.get('Content-Disposition');
      const match = cd && cd.match(/filename="?([^"]+)"?/);
      a.download = match ? match[1] : `${server.name}_world.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
      addToast('Welt-Download gestartet', 'success');
    } catch (e) {
      addToast(e.message, 'error');
    } finally {
      setDownloading(false);
    }
  }

  async function handleUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.zip')) {
      addToast('Nur ZIP-Dateien erlaubt', 'error');
      return;
    }

    if (!window.confirm(
      'ACHTUNG: Die bestehende Welt wird komplett geloescht und durch die hochgeladene Welt ersetzt!\n\n' +
      'Dieser Vorgang kann NICHT rueckgaengig gemacht werden.\n\n' +
      'Fortfahren?'
    )) {
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setUploading(true);
    setUploadProgress('Hochladen...');
    try {
      await api.uploadWorld(server.id, file);
      addToast('Welt erfolgreich hochgeladen und entpackt', 'success');
      setUploadProgress('');
    } catch (e) {
      addToast(e.message, 'error');
      setUploadProgress('');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  const isStopped = server.status === 'stopped';

  return (
    <div className="card">
      <h3>Welt-Verwaltung</h3>

      {/* Download Section */}
      <div className="world-section">
        <h4>Welt herunterladen</h4>
        <p className="text-sm text-muted mb-3">
          Laedt die aktuelle Welt als ZIP-Archiv herunter.
          {server.status === 'running' && ' Der Server wird kurz pausiert (save-off) waehrend des Downloads.'}
        </p>
        <button
          className="btn btn-primary"
          onClick={handleDownload}
          disabled={downloading}
        >
          {downloading ? 'Download laeuft...' : 'Welt herunterladen'}
        </button>
      </div>

      {/* Upload Section */}
      <div className="world-section" style={{ marginTop: 24 }}>
        <h4>Welt hochladen</h4>
        {!isStopped ? (
          <div className="world-warning">
            Server muss gestoppt sein um eine Welt hochzuladen.
          </div>
        ) : (
          <>
            <div className="world-warning world-warning-danger">
              ACHTUNG: Das Hochladen einer Welt loescht die bestehende Welt unwiderruflich!
              Erstelle vorher ein Backup.
            </div>
            <p className="text-sm text-muted mb-3">
              Lade eine ZIP-Datei hoch, die den Welt-Ordner (world/) enthaelt.
              Die ZIP-Datei kann entweder einen "world/"-Unterordner haben oder die Welt-Dateien direkt enthalten.
            </p>
            <div className="world-upload-area">
              <input
                ref={fileInputRef}
                type="file"
                accept=".zip"
                onChange={handleUpload}
                disabled={uploading}
                style={{ display: 'none' }}
                id="world-upload-input"
              />
              <label htmlFor="world-upload-input" className={`btn ${uploading ? 'btn-secondary' : 'btn-warning'}`} style={{ cursor: uploading ? 'wait' : 'pointer' }}>
                {uploading ? uploadProgress || 'Hochladen...' : 'ZIP-Datei auswaehlen und hochladen'}
              </label>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ServerBlueMap({ server }) {
  const isRunning = server.status === 'running';
  const mapUrl = `/api/bluemap/${server.id}/`;

  if (!isRunning) {
    return (
      <div className="card">
        <h3>Weltkarte (BlueMap)</h3>
        <div className="empty-state">
          <div className="icon">&#127758;</div>
          <p>BlueMap ist nur verfuegbar, wenn der Server laeuft.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12 }}>
        <h3 style={{ margin: 0 }}>Weltkarte (BlueMap)</h3>
        <a href={mapUrl} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-sm">
          In neuem Tab &#8599;
        </a>
      </div>
      <iframe
        src={mapUrl}
        title={`BlueMap - ${server.name}`}
        style={{ width: '100%', height: '600px', border: 'none', display: 'block' }}
        allowFullScreen
      />
    </div>
  );
}

function formatUptime(ms) {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  return `${minutes}m`;
}

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default ServerDetail;
